import { useQuery } from '@tanstack/react-query'
import { useCurrentNetwork } from '@mysten/dapp-kit-react'
import { Position } from '@kunalabs-io/kai'
import { fromBase64 } from '@mysten/sui/utils'

import { useBackendClient } from '@/shared/kai/contexts/BackendClientContext'
import {
  OrderType,
  PositionsResponseType,
  PositionsSortByType,
  PositionsStatusFilterType,
  TimeFilterType,
} from '@/shared/backend/types/leaderboard'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import {
  PositionDisplayInfo,
  useUserPositionDisplayInfos,
} from 'utils/userUserPositionDisplayInfos'

export const leaderboardPositionsQueryKey = (
  network: string,
  sortBy: PositionsSortByType,
  order: OrderType,
  timeFilter: TimeFilterType,
  statusFilter: PositionsStatusFilterType,
  isStablePair: boolean | null
) =>
  ['leaderboard-positions', network, sortBy, order, timeFilter, statusFilter, isStablePair] as const

export type LeaderboardPositionType = PositionsResponseType[number]

export interface LeaderboardPosition extends LeaderboardPositionType {
  displayInfo: PositionDisplayInfo
  rank: number
}

export const useLeaderboardPositions = (
  hydrated: boolean,
  sortBy: PositionsSortByType,
  order: OrderType,
  timeFilter: TimeFilterType,
  statusFilter: PositionsStatusFilterType,
  isStablePair: boolean | null
) => {
  const network = useCurrentNetwork()

  const backendClient = useBackendClient()

  const result = useQuery({
    enabled: hydrated,
    queryKey: leaderboardPositionsQueryKey(
      network,
      sortBy,
      order,
      timeFilter,
      statusFilter,
      isStablePair
    ),
    queryFn: async () => {
      return await backendClient.leaderboardGetPositions(
        sortBy,
        order,
        timeFilter,
        statusFilter === 'closed' ? 'all' : 'open',
        isStablePair
      )
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 1 * 60 * 1000, // 1 minute
  })

  const positionInfos: PositionInfo[] = []

  result.data?.forEach(p => {
    positionInfos.push({
      positionId: p.positionId,
      lastPosition: Position.fromBcs(fromBase64(p.lastPositionBcs), p.positionType),
      isDeleted: p.status === 'deleted',
      positionCapId: '',
      creationTimestampMs: p.positionCreationTimestampMs,
    })
  })

  const { positionDisplayInfos, isLoading: isLoadingDisplayInfo } =
    useUserPositionDisplayInfos(positionInfos)

  const leaderboardPositions: LeaderboardPosition[] = []
  result.data?.forEach((leaderboardPosition, index) => {
    const displayInfo = positionDisplayInfos.find(
      p => p.position.id === leaderboardPosition.positionId
    )
    if (displayInfo) {
      leaderboardPositions.push({
        ...leaderboardPosition,
        displayInfo,
        rank: index + 1,
      })
    }
  })

  return {
    isLoading: result.isLoading || isLoadingDisplayInfo,
    isError: result.isError,
    data: leaderboardPositions,
  }
}
